import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, HeartHandshake, MapPin, BadgeCheck } from "lucide-react";
import { IMAGES } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Enhanced DBS-Checked Companions" },
  { icon: HeartHandshake, label: "Personality-First Matching" },
  { icon: MapPin, label: "Across the United Kingdom" },
];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-32 lg:pb-32 lg:pt-44"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-mauve/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-sage/20 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:gap-10 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div {...fadeUp(0.1)}>
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-mauve/30 bg-mauve-light px-4 py-2 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.25em] text-mauve-dark"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" />
              Non-Medical Companionship · UK
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.25}>Compassionate</MaskedLine>
            <MaskedLine delay={0.4}>Companionship</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="italic text-mauve-dark">You Can Trust.</span>
            </MaskedLine>
          </h1>

          <motion.p
            {...fadeUp(0.75)}
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/75 sm:text-lg"
          >
            Bringing comfort, connection and companionship to every moment. Thoughtful,
            dignified support for older adults across the UK — rekindling daily joy,
            stimulating conversation, and reassurance for the whole family.
          </motion.p>

          <motion.div {...fadeUp(0.9)} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onBook}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-cream shadow-elevated transition-all duration-300 hover:-translate-y-0.5 hover:bg-mauve-dark"
            >
              Book a Consultation
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </button>
            <a
              href="#services"
              onClick={(e) => scrollToSection(e, "#services")}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white/70 px-8 py-4 text-sm font-semibold text-navy backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-mauve/40 hover:bg-mauve-light"
            >
              Learn More
            </a>
          </motion.div>

          <motion.ul {...fadeUp(1.05)} className="mt-12 flex flex-wrap gap-x-8 gap-y-4">
            {TRUST_BADGES.map(({ icon: Icon, label }, i) => (
              <li
                key={label}
                data-testid={`hero-trust-badge-${i + 1}`}
                className="flex items-center gap-2.5 text-sm font-medium text-navy/70"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
            className="relative mx-auto max-w-md lg:max-w-none"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-8 rounded-full bg-gradient-to-br from-mauve/25 via-gold/15 to-sage/25 blur-3xl"
            />
            <svg
              aria-hidden="true"
              viewBox="0 0 80 40"
              className="absolute -top-9 left-1/2 z-10 h-10 w-20 -translate-x-1/2"
            >
              <path d="M8 36 A34 34 0 0 1 72 36" fill="none" stroke="#D4A84B" strokeWidth="4" strokeLinecap="round" />
              <line x1="40" y1="2" x2="40" y2="12" stroke="#D4A84B" strokeWidth="3.4" strokeLinecap="round" />
              <line x1="16" y1="10" x2="22" y2="18" stroke="#D4A84B" strokeWidth="3.4" strokeLinecap="round" />
              <line x1="64" y1="10" x2="58" y2="18" stroke="#D4A84B" strokeWidth="3.4" strokeLinecap="round" />
            </svg>

            <motion.div
              style={{ y: imageY }}
              className="relative aspect-[4/5] overflow-hidden rounded-b-[2.5rem] rounded-t-[12rem] shadow-spotlight ring-1 ring-navy/10"
              data-testid="hero-image-frame"
            >
              <img
                src={IMAGES.hero}
                alt="An Adeya companion sharing tea and warm conversation with an elderly client on a park bench"
                className="h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent"
              />
            </motion.div>

            <motion.div
              style={{ y: cardY }}
              data-testid="hero-floating-badge"
              className="absolute -left-4 bottom-12 animate-float-soft rounded-2xl border border-navy/[0.06] bg-white/90 p-4 shadow-elevated backdrop-blur-md sm:-left-10"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage-light text-sage-dark">
                  <BadgeCheck className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-navy">Enhanced DBS Checked</p>
                  <p className="text-xs text-charcoal/60">Every Adeya companion, always</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
